import './belege.css';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fmtDateTime, monthName, plural } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { BelegDialog, CATEGORY_LABEL } from './BelegDialog';
import type { BarsResponse } from './types';

// Hinweisleisten „Neu hochgeladen“ / „Beleg gelöscht“ und Beleg-Kommentare von Mandanten.
// Ohne clientId: alle sichtbaren Mandanten (Übersicht). Mit clientId: nur dieser Mandant (Mandantenansicht).
export function BelegeBars({ clientId }: { clientId?: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const url = `/api/kanzlei/belege/bars${clientId ? `?clientId=${clientId}` : ''}`;
  const res = useApi<BarsResponse>(url, { live: ['belege'] });
  const [open, setOpen] = useState<string | null>(null);
  const d = res.data;

  const seen = async (ids: string[]) => {
    try {
      await api.post('/api/kanzlei/belege/seen', { ids });
      await res.reload();
    } catch (err) {
      toast(errorText(err));
    }
  };

  // „Alle als gesehen“ markiert auf dem Server (ohne ID-Liste, auch bei sehr vielen Belegen),
  // aber nur, was bis zum Stand der Leiste da war
  const seenAll = async (until: string) => {
    try {
      await api.post('/api/kanzlei/belege/seen-all', { clientId, until });
      await res.reload();
      toast('Alles als gesehen markiert.');
    } catch (err) {
      toast(errorText(err));
    }
  };

  // Admin: wer ist für die FiBu zuständig? „offen“ rot, damit zugewiesen wird
  const who = (fibu: string | null | undefined) =>
    d?.admin && fibu !== undefined ? (
      <span className={`note${fibu ? '' : ' who-open'}`}> · FiBu: {fibu ?? 'offen, bitte zuweisen'}</span>
    ) : null;

  if (!d) return open ? <BelegDialog id={open} onClose={() => setOpen(null)} /> : null;

  const upRows = d.uploads.map((g) => {
    const subs = [...new Set(g.belege.map((b) => CATEGORY_LABEL[b.category]))];
    const names = g.belege.slice(0, 3).map((b) => (b.changeNote ? `${b.name} (${b.changeNote})` : b.name));
    return (
      <div className="mb-r" key={`u-${g.clientId}-${g.year}-${g.month}`}>
        <div className="tx">
          <b>{g.clientName}</b> · {plural(g.belege.length, 'Beleg', 'Belege')} für {monthName(g.month)} {g.year}
          {who(g.fibu)}
          <small>
            {names.join(', ')}
            {g.belege.length > 3 ? ' …' : ''} · Belege › {subs.join(', ')}
          </small>
        </div>
        <button
          type="button"
          className="btn sm dark"
          onClick={() => {
            void seen(g.belege.map((b) => b.id));
            navigate(`/kanzlei/mandanten/${g.clientId}/belege?jahr=${g.year}&monat=${g.month}`);
          }}
        >
          Ansehen
        </button>
      </div>
    );
  });
  const goneRows = d.deleted.map((b) => (
    <div className="mb-r" key={`d-${b.id}`}>
      <div className="tx">
        <b>{b.clientName}</b> · Beleg gelöscht: {b.name}
        {who(b.fibu)}
        <small>
          {b.deletedBy} hat den Beleg in der App gelöscht · war unter {monthName(b.month)} {b.year} › {CATEGORY_LABEL[b.category]}
        </small>
      </div>
      <button type="button" className="btn sm" onClick={() => void seen([b.id])}>
        Ok
      </button>
    </div>
  ));
  const all = [...upRows, ...goneRows];
  const until = d.seenUntil;

  const cmRows = d.comments.map((c) => (
    <div className="mb-r" key={`c-${c.belegId}`}>
      <div className="tx">
        <b>{c.authorName}</b> · Beleg {c.belegName}
        {!clientId && <span className="note"> · {c.clientName}</span>}
        {who(c.fibu)}
        <small>
          „{c.body}“ · {fmtDateTime(c.createdAt)}
        </small>
      </div>
      <button
        type="button"
        className="btn sm dark"
        onClick={() => {
          api
            .post(`/api/kanzlei/beleg/${c.belegId}/ack`)
            .then(() => res.reload())
            .catch(() => {});
          setOpen(c.belegId);
        }}
      >
        Antworten
      </button>
    </div>
  ));

  return (
    <>
      {all.length > 0 && (
        <Bar
          head={
            <>
              📎 {upRows.length ? 'Neu hochgeladen' : 'Beleg gelöscht'}
              {all.length > 1 && until && (
                <button type="button" className="btn sm mb-all" onClick={() => void seenAll(until)}>
                  Alle als gesehen
                </button>
              )}
            </>
          }
          rows={all}
        />
      )}
      {cmRows.length > 0 && (
        <Bar head={<>💬 {cmRows.length === 1 ? 'Neuer Kommentar zu einem Beleg' : 'Neue Kommentare von Mandanten'}</>} rows={cmRows} />
      )}
      {open && <BelegDialog id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

// Leiste mit Überschrift; lange Leisten zeigen erst drei Zeilen
function Bar({ head, rows }: { head: ReactNode; rows: ReactNode[] }) {
  const [all, setAll] = useState(false);
  const cut = rows.length > 4 && !all;
  return (
    <div className="msgbar" role="status">
      <div className="mb-h mb-hf">{head}</div>
      {cut ? rows.slice(0, 3) : rows}
      {rows.length > 4 && (
        <button type="button" className="btn sm mb-more" aria-expanded={!cut} onClick={() => setAll((x) => !x)}>
          {cut ? `Alle anzeigen (${rows.length})` : 'Weniger anzeigen'}
        </button>
      )}
    </div>
  );
}
